/**
 * 5-state qualitative assessment ladder (DEV-1075 Path A).
 *
 * Used by Mission KPI evaluation when scoring_mode='qualitative' —
 * currently the Mini-Project ownership tier. The Lead picks one of
 * five states per goal at every Status update:
 *
 *   ahead     - trending better than expected, still in progress
 *   on_track  - in progress, going as expected
 *   behind    - in progress, lagging plan
 *   blocked   - in progress, can't make headway, needs intervention
 *   done      - explicitly marked complete by the Lead
 *
 * Pre-DEV-1075 the same ladder existed inline in two component files
 * (MissionModal, StatusReader). This module is the consolidated source
 * of truth — both surfaces should call into it rather than maintain
 * parallel copies. The mission KPI engine also uses it via the
 * scoring_mode='qualitative' branch.
 *
 * Mirrors the prose in:
 *   - src/Life4Earth.V2.Api/Models/MiniProjectStatusGoalAssessment.cs
 *     (where MiniProjectAssessment constants live on the backend)
 *   - migrations/177_unify_mission_kpi.sql §2 chk_metric_points_assessment
 */

export type QualitativeAssessment =
  | 'ahead'
  | 'on_track'
  | 'behind'
  | 'blocked'
  | 'done';

export const QUALITATIVE_ASSESSMENTS: ReadonlyArray<QualitativeAssessment> = [
  'ahead',
  'on_track',
  'behind',
  'blocked',
  'done',
] as const;

export function isQualitativeAssessment(
  v: unknown,
): v is QualitativeAssessment {
  return (
    typeof v === 'string' &&
    (QUALITATIVE_ASSESSMENTS as readonly string[]).includes(v)
  );
}

/**
 * Per-assessment score (0-100). Drops into the Mission rollup's
 * weighted average alongside numeric scores from scoreNumeric et al.
 *
 *   blocked   →   0  (red — no progress, intervention needed)
 *   behind    →  35  (yellow — lagging the plan)
 *   on_track  →  80  (green — proceeding to plan)
 *   ahead     → 100  (green — exceeding plan)
 *   done      → 100  (complete; rendering layer may promote to blue)
 *
 * The 35 / 80 split intentionally sits just below the engine's
 * universal yellow_threshold (40) and green_threshold (80) bands so
 * the qualitative-derived numeric score lands in the same color
 * bucket as the assessmentToColorValue() output below — keeping the
 * two derivations internally consistent.
 */
export function assessmentToScore(a: QualitativeAssessment): number {
  switch (a) {
    case 'blocked':
      return 0;
    case 'behind':
      return 35;
    case 'on_track':
      return 80;
    case 'ahead':
      return 100;
    case 'done':
      return 100;
  }
}

/**
 * Per-goal indicator color value, using the same 0-3 vocabulary as
 * the numeric path (see colorValueFromScore in mission.ts and
 * defineIndicatorColor in src/utils/helpers.tsx):
 *
 *   1 = red    (blocked)
 *   2 = yellow (behind)
 *   3 = green  (on_track | ahead | done)
 *
 * 'done' maps to green at the engine layer. Blue ("completed") is an
 * overlay applied by the rendering layer when the caller signals
 * completion separately — same convention as DEV-966 project-level
 * completion rendering. The engine does not emit 4=blue.
 */
export function assessmentToColorValue(
  a: QualitativeAssessment,
): 1 | 2 | 3 {
  switch (a) {
    case 'blocked':
      return 1;
    case 'behind':
      return 2;
    case 'on_track':
    case 'ahead':
    case 'done':
      return 3;
  }
}

/**
 * Multi-assessment rollup color — used by Mission section aggregates
 * (the color the Mission tab shows above the per-goal cards) and by
 * the Performance Summary's Mission status pill.
 *
 *   any blocked       → red    (1)
 *   any behind        → yellow (2)
 *   any in-progress   → green  (3)   (ahead | on_track)
 *   all assessments are 'done' → green (3) + allDone:true
 *                                     (caller may promote to blue)
 *   empty input       → grey   (0)
 *
 * Returns the 0-3 color value AND a separate `allDone` flag so callers
 * can decide whether to promote 3 → blue per the completion overlay
 * convention. Mirrors the prose in MiniProjectStatusGoalAssessment.cs
 * and the inline aggregators in MissionModal / StatusReader.
 */
export function aggregateAssessmentsToColor(
  assessments: ReadonlyArray<QualitativeAssessment>,
): { colorValue: 0 | 1 | 2 | 3; allDone: boolean } {
  if (assessments.length === 0) {
    return { colorValue: 0, allDone: false };
  }

  let blocked = 0;
  let behind = 0;
  let inProgress = 0;
  let done = 0;

  for (const a of assessments) {
    if (a === 'blocked') blocked++;
    else if (a === 'behind') behind++;
    else if (a === 'ahead' || a === 'on_track') inProgress++;
    else if (a === 'done') done++;
  }

  if (blocked > 0) return { colorValue: 1, allDone: false };
  if (behind > 0) return { colorValue: 2, allDone: false };
  if (inProgress > 0) return { colorValue: 3, allDone: false };
  // All done — same as 'green' at the color-value layer; allDone signals
  // the rendering overlay opportunity.
  return { colorValue: 3, allDone: true };
}
